import WebFont from "webfontloader";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { Chart } from "chart.js";
import "bootstrap/dist/css/bootstrap.css";

import "./style/tokens.css";
import "./style/index.css";
import App from "./containers/App";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import ProviderComponent from "./containers/Provider";
import { ThemeProvider } from "./context/ThemeContext";
import ErrorBoundary from "./components/ErrorBoundary";

import posthog from "posthog-js";
import { PostHogProvider, PostHogErrorBoundary } from "@posthog/react";

posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY!, {
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  // Ingestion routes through the j.dejachart.com reverse proxy (api_host). ui_host must
  // point at the real PostHog cloud so toolbar / session-replay / dashboard links resolve
  // to PostHog rather than the proxy domain.
  ui_host: "https://us.posthog.com",
  // Single-domain SPA: skip posthog-js's widest-cookie-domain probe (its rejected
  // dmn_chk_* test cookie logs a "rejected for invalid domain" warning in Firefox).
  // Identity doesn't need to span subdomains — j.dejachart.com is only an API proxy.
  cross_subdomain_cookie: false,
  defaults: "2026-01-30",
  loaded: (ph) => {
    if (process.env.REACT_APP_ENV !== "production") ph.opt_out_capturing();
  },
});

WebFont.load({
  google: {
    families: ["Inter:300,400,500,600,700", "IBM Plex Mono:400,500", "Manrope:500,600,700,800"],
  },
});

const font = "Inter, IBM Plex Mono, sans-serif";
Chart.defaults.font.family = font;

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <PostHogProvider client={posthog}>
    <PostHogErrorBoundary>
      <ThemeProvider>
        <ProviderComponent>
          <BrowserRouter>
            <ScrollToTop />
            <Layout>
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </Layout>
          </BrowserRouter>
        </ProviderComponent>
      </ThemeProvider>
    </PostHogErrorBoundary>
  </PostHogProvider>,
);

// The legacy CRA service worker was removed (react-scripts 5 never emitted
// /service-worker.js, so registration failed on every visit — and in Firefox
// strict/private mode logged "DOMException: The operation is insecure").
// Defensively unregister any stale worker left behind by much older builds.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => void r.unregister()))
    .catch(() => {
      /* storage-restricted contexts (e.g. Firefox private mode) throw — ignore */
    });
}
